import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Camera, X } from "lucide-react";

/**
 * CameraQrScanner — modal lid with camera feed that decodes QR codes via html5-qrcode.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onScan: (decodedText) => void
 *  - testId: string
 */
export default function CameraQrScanner({ open, onClose, onScan, testId = "camera-qr-scanner" }) {
  const containerRef = useRef(null);
  const qrRef = useRef(null);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setErr(null);

    const start = async () => {
      try {
        const id = "camera-qr-region";
        if (containerRef.current && !containerRef.current.querySelector(`#${id}`)) {
          const region = document.createElement("div");
          region.id = id;
          region.style.width = "100%";
          region.style.height = "100%";
          containerRef.current.appendChild(region);
        }
        const qr = new Html5Qrcode(id, { verbose: false });
        qrRef.current = qr;
        let devices = [];
        try { devices = await Html5Qrcode.getCameras(); } catch { devices = []; }
        if (!devices || devices.length === 0) {
          if (!cancelled) setErr("Aucune caméra détectée ou accès refusé");
          return;
        }
        const cameraId = devices.find((d) => /back|rear|environment/i.test(d.label))?.id || devices[0].id;
        await qr.start(
          cameraId,
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (cancelled) return;
            onScan && onScan(decodedText);
          },
          () => {} // per-frame decode errors suppressed
        );
        if (!cancelled) setRunning(true);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Impossible d'ouvrir la caméra");
      }
    };

    start();

    return () => {
      cancelled = true;
      const qr = qrRef.current;
      qrRef.current = null;
      setRunning(false);
      if (!qr) return;
      // Only stop if currently scanning/paused — html5-qrcode throws otherwise
      try {
        const state = typeof qr.getState === "function" ? qr.getState() : null;
        const isActive = state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED;
        if (isActive) {
          qr.stop()
            .then(() => { try { qr.clear(); } catch { /* noop */ } })
            .catch(() => { try { qr.clear(); } catch { /* noop */ } });
        } else {
          try { qr.clear(); } catch { /* noop */ }
        }
      } catch { /* noop */ }
    };
  }, [open, onScan]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid={testId}>
      <div className="bg-white border border-zinc-900 w-full max-w-md">
        <div className="flex items-center justify-between border-b border-zinc-900 p-3">
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <Camera size={18} /> Scanner QR — caméra
          </div>
          <button onClick={onClose} data-testid="camera-scanner-close"><X size={18} /></button>
        </div>
        <div className="relative bg-black" style={{ aspectRatio: "1 / 1" }}>
          <div ref={containerRef} className="absolute inset-0" />
          {!running && !err && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-mono tracking-widest uppercase">
              Activation caméra…
            </div>
          )}
          {err && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm p-4 text-center" data-testid="camera-error">
              <div className="font-display font-bold text-base">Caméra indisponible</div>
              <div className="text-xs text-zinc-300 mt-1">{err}</div>
              <div className="text-[10px] font-mono text-zinc-400 mt-3 uppercase tracking-widest">Autorisez l'accès caméra puis ré-ouvrez.</div>
            </div>
          )}
          {running && (
            <div className="absolute inset-8 pointer-events-none border-2 border-[#FFC60A]" style={{ clipPath: "polygon(0 0, 25% 0, 25% 3%, 3% 3%, 3% 25%, 0 25%, 0 75%, 3% 75%, 3% 97%, 25% 97%, 25% 100%, 75% 100%, 75% 97%, 97% 97%, 97% 75%, 100% 75%, 100% 25%, 97% 25%, 97% 3%, 75% 3%, 75% 0)" }} />
          )}
        </div>
        <div className="p-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500">Placez le QR de l'enfant dans le cadre jaune.</div>
      </div>
    </div>
  );
}
